import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Shield, Database, Activity, Trash2, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";

interface SystemInfo {
  database_size: string;
  active_connections: number;
  cache_status: 'healthy' | 'warning' | 'error';
  last_backup: string;
  uptime: string;
}

interface UserRole {
  id: string;
  user_id: string;
  role: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export const SystemManagementSection = () => {
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [systemInfo, setSystemInfo] = useState<SystemInfo>({
    database_size: '0 MB',
    active_connections: 0,
    cache_status: 'healthy',
    last_backup: new Date().toISOString(),
    uptime: '0 days'
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchUserRoles = async () => {
    try {
      // Get user roles first
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('*')
        .order('created_at', { ascending: false });

      if (rolesError) throw rolesError;

      if (!roles || roles.length === 0) {
        setUserRoles([]);
        return;
      }

      // Get user IDs
      const userIds = roles.map(role => role.user_id);

      // Get profiles for those users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Combine roles with profile data
      const combinedData = roles.map(role => {
        const profile = profiles?.find(p => p.id === role.user_id);
        return {
          ...role,
          first_name: profile?.first_name || '',
          last_name: profile?.last_name || '',
          email: profile?.email || ''
        };
      });

      setUserRoles(combinedData);
    } catch (error) {
      console.error('Error fetching user roles:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել օգտատերերի դերերը",
        variant: "destructive"
      });
    }
  };

  const fetchSystemInfo = async () => {
    try {
      // Simulate system info (in a real app, this would come from actual system metrics)
      const startTime = new Date('2024-01-01').getTime();
      const currentTime = new Date().getTime();
      const uptimeDays = Math.floor((currentTime - startTime) / (1000 * 60 * 60 * 24));

      setSystemInfo({
        database_size: '125 MB',
        active_connections: Math.floor(Math.random() * 50) + 10,
        cache_status: 'healthy',
        last_backup: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
        uptime: `${uptimeDays} days`
      });
    } catch (error) {
      console.error('Error fetching system info:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchUserRoles(), fetchSystemInfo()]);
      setLoading(false);
    };

    loadData();
  }, []);

  const handleClearCache = async () => {
    try {
      // Simulate cache clearing
      toast({
        title: "Հաջողություն",
        description: "Cache-ը մաքրվեց հաջողությամբ"
      });
      
      // Refresh system info
      await fetchSystemInfo();
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast({
        title: "Սխալ",
        description: "Cache-ը մաքրելիս տեղի ունեցավ սխալ",
        variant: "destructive"
      });
    }
  };

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      admin: { label: 'Ադմին', className: 'bg-red-500/20 text-red-300 border-red-400/30' },
      lecturer: { label: 'Դասախոս', className: 'bg-blue-500/20 text-blue-300 border-blue-400/30' },
      student: { label: 'Ուսանող', className: 'bg-primary/10 text-primary border-border/70' }
    };
    
    const config = roleConfig[role as keyof typeof roleConfig] || 
      { label: role, className: 'bg-muted/50 text-muted-foreground border-border' };
    
    return (
      <Badge className={`${config.className}`}>
        {config.label}
      </Badge>
    );
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-4 w-4 text-primary" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-400" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-400" />;
      default:
        return <CheckCircle className="h-4 w-4 text-primary" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('hy-AM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Բեռնվում է...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* System Health */}
      <Card className="border-border/70 bg-card/80">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Համակարգի վիճակ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-blue-500/20 border border-blue-400/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Տվյալների բազայի չափ</p>
                    <p className="text-blue-300 text-xl font-bold">{systemInfo.database_size}</p>
                  </div>
                  <Database className="h-8 w-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-green-500/20 border border-border/70">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Ակտիվ կապեր</p>
                    <p className="text-muted-foreground text-xl font-bold">{systemInfo.active_connections}</p>
                  </div>
                  <Activity className="h-8 w-8 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-purple-500/20 border border-purple-400/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Cache վիճակ</p>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(systemInfo.cache_status)}
                      <p className="text-purple-300 text-lg font-bold">
                        {systemInfo.cache_status === 'healthy' ? 'Առողջ' : 'Խնդիր'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-yellow-500/20 border border-yellow-400/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-muted-foreground text-sm">Համակարգի աշխատանք</p>
                    <p className="text-yellow-300 text-xl font-bold">{systemInfo.uptime}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-yellow-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:gap-4">
            <Button
              onClick={handleClearCache}
              variant="outline"
              className="min-h-11 w-full border-border/70 text-foreground hover:bg-muted sm:w-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Մաքրել Cache
            </Button>
            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              className="min-h-11 w-full border-border/70 text-foreground hover:bg-muted sm:w-auto"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Թարմացնել
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* User Roles Management */}
      <Card className="border-border/70 bg-card/80">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Օգտատերերի դերեր ({userRoles.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {userRoles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Օգտատերեր չկան
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[680px]">
                <TableHeader>
                  <TableRow className="border-border/70">
                    <TableHead className="text-muted-foreground">Օգտատեր</TableHead>
                    <TableHead className="text-muted-foreground">Էլ. փոստ</TableHead>
                    <TableHead className="text-muted-foreground">Դեր</TableHead>
                    <TableHead className="text-muted-foreground">Ավելացման ամսաթիվ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {userRoles.map((userRole) => (
                    <TableRow key={userRole.id} className="border-border/70">
                      <TableCell className="text-muted-foreground">
                        {userRole.first_name} {userRole.last_name}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {userRole.email}
                      </TableCell>
                      <TableCell>
                        {getRoleBadge(userRole.role)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(userRole.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Actions */}
      <Card className="border-border/70 bg-card/80">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Database className="h-5 w-5" />
            Համակարգային գործողություններ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-muted/50 border border-border p-4">
              <h3 className="text-muted-foreground font-bold mb-2">Վերջին backup</h3>
              <p className="text-muted-foreground text-sm mb-4">
                {formatDate(systemInfo.last_backup)}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="min-h-11 w-full border-border/70 text-primary hover:bg-primary/10"
              >
                Backup ստեղծել
              </Button>
            </Card>

            <Card className="bg-muted/50 border border-border p-4">
              <h3 className="text-muted-foreground font-bold mb-2">Գործունեության մատյան</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Տեսնել բոլոր գործողությունները
              </p>
              <Button
                variant="outline"
                size="sm"
                className="min-h-11 w-full border-border/70 text-foreground hover:bg-muted"
              >
                Տեսնել մատյանը
              </Button>
            </Card>

            <Card className="bg-muted/50 border border-border p-4">
              <h3 className="text-muted-foreground font-bold mb-2">Anվտանգության բծեր</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Ստուգել անվտանգությունը
              </p>
              <Button
                variant="outline"
                size="sm"
                className="min-h-11 w-full border-destructive/40 text-destructive hover:bg-destructive/10"
              >
                Անվտանգության ստուգում
              </Button>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
